import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Play, X, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';
import { servicesData } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create a mapping of common service terms to actual service slugs
const serviceMapping = {
  'brand launch': 'brand-identity',
  'brand identity': 'brand-identity',
  'branding': 'brand-identity',
  'visual identity': 'brand-identity',
  'event coverage': 'event-broadcasting',
  'event broadcasting': 'event-broadcasting',
  'event photography': 'event-broadcasting',
  'content creation': 'content-creation',
  'content strategy': 'content-strategy',
  'social media': 'content-creation',
  'social media strategy': 'content-creation',
  'marketing materials': 'graphic-design',
  'marketing': 'digital-advertising',
  'video production': 'film-production',
  'film production': 'film-production',
  'cinematography': 'film-production',
  'brand story': 'brand-identity',
  'photo editing': 'graphic-design',
  'portrait photography': 'film-production',
  'website': 'website-development',
  'web development': 'website-development',
  'seo': 'seo-strategies',
  'motion graphics': 'motion-graphics',
  'graphic design': 'graphic-design',
  'ai automation': 'ai-automations',
  'automation': 'ai-automations',
  'digital advertising': 'digital-advertising',
  'real estate': 'real-estate-media',
  'real estate media': 'real-estate-media',
  'drone': 'real-estate-media',
  'wedding': 'wedding-cinematography',
  'wedding cinematography': 'wedding-cinematography',
  'media strategy': 'media-strategy',
  'technology advisory': 'technology-advisory',
  'budget': 'budget-appraisals',
  'audience audit': 'audience-audit',
  'automation consulting': 'automation-consulting',
};

// Map service names to their slugs
const getServiceSlug = (serviceName) => {
  const normalizedName = serviceName.toLowerCase().trim();
  
  // First check the direct mapping
  if (serviceMapping[normalizedName]) {
    return serviceMapping[normalizedName];
  }
  
  // Check for partial matches in the mapping
  for (const [key, slug] of Object.entries(serviceMapping)) {
    if (normalizedName.includes(key) || key.includes(normalizedName)) {
      return slug;
    }
  }
  
  // Check against actual services in mock data
  for (const category of servicesData) {
    for (const service of category.services) {
      if (service.name.toLowerCase() === normalizedName ||
          service.slug === normalizedName.replace(/\s+/g, '-') ||
          normalizedName.includes(service.name.toLowerCase()) ||
          service.name.toLowerCase().includes(normalizedName)) {
        return service.slug;
      }
    }
  }
  return null;
};

// Image Gallery Modal
const GalleryModal = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeout = useRef(null);

  const handlePrev = () => setCurrentIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
  const handleNext = () => setCurrentIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));

  const resetControlsTimeout = () => {
    setShowControls(true);
    if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
    controlsTimeout.current = setTimeout(() => setShowControls(false), 3000);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    resetControlsTimeout();
    return () => {
      window.removeEventListener('keydown', handleKey);
      if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
    };
  }, [onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      onClick={resetControlsTimeout}
    >
      <img 
        src={images[currentIndex]?.url} 
        alt={images[currentIndex]?.caption || ''} 
        className="max-w-full max-h-full object-contain"
      />
      
      <div className={`absolute inset-0 pointer-events-none transition-opacity ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white pointer-events-auto">
          <X className="w-6 h-6" />
        </button>
        
        {/* Navigation */}
        {images.length > 1 && (
          <>
            <button onClick={handlePrev} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white pointer-events-auto">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button onClick={handleNext} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white pointer-events-auto">
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
        
        {/* Caption & Counter */}
        <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-auto">
          {images[currentIndex]?.caption && (
            <p className="text-white/80 text-sm mb-2">{images[currentIndex].caption}</p>
          )}
          <p className="text-white/50 text-xs">{currentIndex + 1} / {images.length}</p>
        </div>
      </div>
    </div>
  );
};

// Video Modal
const VideoModal = ({ video, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" onClick={onClose}>
      <div className="w-full max-w-4xl aspect-video relative" onClick={e => e.stopPropagation()}>
        <video src={video.url} controls autoPlay playsInline className="w-full h-full" />
      </div>
      <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white">
        <X className="w-6 h-6" />
      </button>
    </div>
  );
};

// Scrollable Gallery Component
const ScrollableGallery = ({ images, onImageClick }) => {
  const scrollRef = useRef(null);
  
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  if (!images || images.length === 0) return null;

  return (
    <div className="relative group">
      {/* Scroll Buttons */}
      <button 
        onClick={() => scroll('left')}
        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button 
        onClick={() => scroll('right')}
        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      
      {/* Gallery */}
      <div 
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {images.map((img, idx) => (
          <div 
            key={idx}
            onClick={() => onImageClick(idx)}
            className="flex-shrink-0 w-64 sm:w-80 aspect-[4/5] relative cursor-pointer snap-start overflow-hidden group/item"
          >
            <img 
              src={img.url} 
              alt={img.caption || `Gallery image ${idx + 1}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover/item:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover/item:bg-black/20 transition-colors" />
          </div>
        ))}
      </div>
    </div>
  );
};

// Scrollable Videos Component
const ScrollableVideos = ({ videos, onVideoClick }) => {
  const scrollRef = useRef(null);
  
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  if (!videos || videos.length === 0) return null;

  return (
    <div className="relative group">
      {videos.length > 2 && (
        <>
          <button 
            onClick={() => scroll('left')}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button 
            onClick={() => scroll('right')}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
      
      <div 
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {videos.map((video, idx) => (
          <div 
            key={idx}
            onClick={() => onVideoClick(video)}
            className="flex-shrink-0 w-72 sm:w-96 aspect-video relative cursor-pointer snap-start overflow-hidden group/item bg-white/5"
          >
            {video.thumbnail ? (
              <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-white/10 to-white/5" />
            )}
            
            {/* Play overlay */}
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover/item:bg-black/50 transition-colors">
              <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Play className="w-6 h-6 text-white fill-white" />
              </div>
            </div>
            
            {/* Title */}
            {video.title && (
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-white text-sm font-medium">{video.title}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const StoryDetailPage = () => {
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryModal, setGalleryModal] = useState(null);
  const [videoModal, setVideoModal] = useState(null);
  const [allStories, setAllStories] = useState([]);

  useEffect(() => {
    fetchStory();
    fetchAllStories();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchStory = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stories/${slug}`);
      if (res.ok) {
        setStory(await res.json());
      }
    } catch (e) {
      console.error('Error fetching story:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stories`);
      if (res.ok) {
        setAllStories(await res.json());
      }
    } catch (e) {
      console.error('Error fetching stories:', e);
    }
  };

  // Get next/prev stories
  const currentIndex = allStories.findIndex(s => s.slug === slug);
  const prevStory = currentIndex > 0 ? allStories[currentIndex - 1] : null;
  const nextStory = currentIndex < allStories.length - 1 ? allStories[currentIndex + 1] : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-white/40">Loading story...</p>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/40 mb-4">Story not found</p>
          <Link to="/stories" className="text-white underline">Back to Stories</Link>
        </div>
      </div>
    );
  }

  // Parse content into paragraphs
  const paragraphs = story.content.split('\n\n').filter(p => p.trim());

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO 
        title={`${story.client_name} - Success Story`}
        description={story.title}
        keywords={`${story.client_name}, success story, case study, ${story.services_provided?.join(', ')}`}
        canonical={`https://creativedirectorkhan.com/stories/${slug}`}
        ogType="article"
        ogImage={story.hero_image || undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `${story.client_name} - ${story.title}`,
          "description": story.title,
          "image": story.hero_image || '',
          "url": `https://creativedirectorkhan.com/stories/${slug}`,
          "author": { "@type": "Person", "name": "Hassaan Khan" },
          "publisher": { "@type": "Organization", "name": "CreativeDirectorKhan | KHAN", "url": "https://creativedirectorkhan.com" },
          "datePublished": story.created_at || '',
          "dateModified": story.updated_at || story.created_at || '',
          "mainEntityOfPage": `https://creativedirectorkhan.com/stories/${slug}`,
          "keywords": story.services_provided?.join(', ') || ''
        }}
      />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end">
        {/* Background */}
        {story.hero_image ? (
          <div className="absolute inset-0">
            <img src={story.hero_image} alt={story.client_name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
          </div>
        ) : story.hero_video ? (
          <div className="absolute inset-0">
            <video src={story.hero_video} autoPlay muted loop playsInline className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        )}

        {/* Content */}
        <div className="relative z-10 w-full px-6 md:px-12 pb-16 pt-32">
          <div className="max-w-4xl">
            <Link to="/stories" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Stories
            </Link>
            
            <p className="text-white/40 text-xs tracking-[0.3em] uppercase mb-4">Success Story</p>
            <h1 className="font-serif text-5xl md:text-7xl text-white mb-4">{story.client_name}</h1>
            <p className="text-white/60 text-xl md:text-2xl font-light">{story.title}</p>
            
            {/* Services Tags - Clickable links to service pages */}
            {story.services_provided && story.services_provided.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8">
                {story.services_provided.map((service, idx) => {
                  const serviceSlug = getServiceSlug(service);
                  return serviceSlug ? (
                    <Link 
                      key={idx} 
                      to={`/services/${serviceSlug}`}
                      className="px-3 py-1 bg-white/10 text-white/70 text-xs hover:bg-white/20 hover:text-white transition-all"
                    >
                      {service}
                    </Link>
                  ) : (
                    <span key={idx} className="px-3 py-1 bg-white/10 text-white/70 text-xs">
                      {service}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Story Content - Blog Style with Content Blocks */}
      <section className="px-6 md:px-12 py-20">
        <div className="max-w-3xl mx-auto">
          {/* Render content blocks if available, otherwise fall back to plain text */}
          {story.content_blocks && story.content_blocks.length > 0 ? (
            story.content_blocks
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((block, idx) => {
                switch (block.type) {
                  case 'text':
                    return (
                      <p key={idx} className="text-white/70 text-lg leading-relaxed mb-8">
                        {block.content}
                      </p>
                    );
                  case 'image':
                    return (
                      <figure key={idx} className="my-12">
                        <img 
                          src={block.content} 
                          alt={block.alt_text || block.caption || 'Story image'} 
                          className="w-full rounded-sm"
                        />
                        {block.caption && (
                          <figcaption className="text-white/40 text-sm mt-3 text-center italic">
                            {block.caption}
                          </figcaption>
                        )}
                      </figure>
                    );
                  case 'gif':
                    return (
                      <figure key={idx} className="my-12">
                        <img 
                          src={block.content} 
                          alt={block.alt_text || block.caption || 'Animated GIF'} 
                          className="w-full rounded-sm"
                        />
                        {block.caption && (
                          <figcaption className="text-white/40 text-sm mt-3 text-center italic">
                            {block.caption}
                          </figcaption>
                        )}
                      </figure>
                    );
                  case 'video':
                    return (
                      <figure key={idx} className="my-12">
                        <div className="relative aspect-video bg-black rounded-sm overflow-hidden">
                          <video 
                            src={block.content} 
                            controls 
                            playsInline
                            className="w-full h-full object-contain"
                            poster={block.thumbnail || ''}
                          />
                        </div>
                        {block.caption && (
                          <figcaption className="text-white/40 text-sm mt-3 text-center italic">
                            {block.caption}
                          </figcaption>
                        )}
                      </figure>
                    );
                  case 'youtube':
                    // Extract YouTube video ID from URL
                    const youtubeId = block.content.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
                    return youtubeId ? (
                      <figure key={idx} className="my-12">
                        <div className="relative aspect-video bg-black rounded-sm overflow-hidden">
                          <iframe
                            src={`https://www.youtube.com/embed/${youtubeId}`}
                            title={block.caption || 'YouTube video'}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                          />
                        </div>
                        {block.caption && (
                          <figcaption className="text-white/40 text-sm mt-3 text-center italic">
                            {block.caption}
                          </figcaption>
                        )}
                      </figure>
                    ) : null;
                  case 'quote':
                    return (
                      <blockquote key={idx} className="my-12 pl-6 border-l-2 border-white/20">
                        <p className="text-white/80 text-xl italic font-serif leading-relaxed">
                          "{block.content}"
                        </p>
                        {block.caption && (
                          <cite className="text-white/40 text-sm mt-3 block not-italic">
                            — {block.caption}
                          </cite>
                        )}
                      </blockquote>
                    );
                  case 'heading':
                    return (
                      <h2 key={idx} className="font-serif text-2xl md:text-3xl text-white mt-16 mb-6">
                        {block.content}
                      </h2>
                    );
                  default:
                    return (
                      <p key={idx} className="text-white/70 text-lg leading-relaxed mb-8">
                        {block.content}
                      </p>
                    );
                }
              })
          ) : (
            // Fallback: render plain text content as paragraphs
            paragraphs.map((para, idx) => (
              <p key={idx} className="text-white/70 text-lg leading-relaxed mb-8">
                {para}
              </p>
            ))
          )}
        </div>
      </section>

      {/* Podcast Link */}
      {story.podcast_link && (
        <section className="px-6 md:px-12 py-12 border-t border-white/5">
          <div className="max-w-3xl mx-auto">
            <a 
              href={story.podcast_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-6 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group"
            >
              <div>
                <p className="text-white/40 text-xs tracking-wider uppercase mb-1">Listen to our conversation</p>
                <p className="text-white text-lg font-medium">Podcast with {story.client_name}</p>
              </div>
              <ExternalLink className="w-5 h-5 text-white/40 group-hover:text-white transition-colors" />
            </a>
          </div>
        </section>
      )}

      {/* Photo Gallery */}
      {story.gallery_images && story.gallery_images.length > 0 && (
        <section className="py-20 border-t border-white/5">
          <div className="px-6 md:px-12 mb-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-serif text-3xl text-white mb-2">Gallery</h2>
              <p className="text-white/40 text-sm">{story.gallery_images.length} photos</p>
            </div>
          </div>
          <div className="px-6 md:px-12">
            <div className="max-w-6xl mx-auto">
              <ScrollableGallery 
                images={story.gallery_images} 
                onImageClick={(idx) => setGalleryModal(idx)} 
              />
            </div>
          </div>
        </section>
      )}

      {/* Videos Section */}
      {story.videos && story.videos.length > 0 && (
        <section className="py-20 border-t border-white/5">
          <div className="px-6 md:px-12 mb-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-serif text-3xl text-white mb-2">Videos</h2>
              <p className="text-white/40 text-sm">{story.videos.length} videos</p>
            </div>
          </div>
          <div className="px-6 md:px-12">
            <div className="max-w-6xl mx-auto">
              <ScrollableVideos 
                videos={story.videos} 
                onVideoClick={(video) => setVideoModal(video)} 
              />
            </div>
          </div>
        </section>
      )}

      {/* Navigation */}
      <section className="px-6 md:px-12 py-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center">
            {prevStory ? (
              <Link to={`/stories/${prevStory.slug}`} className="group">
                <p className="text-white/30 text-xs uppercase mb-1">Previous Story</p>
                <p className="text-white group-hover:text-white/70 transition-colors flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  {prevStory.client_name}
                </p>
              </Link>
            ) : <div />}
            
            {nextStory ? (
              <Link to={`/stories/${nextStory.slug}`} className="group text-right">
                <p className="text-white/30 text-xs uppercase mb-1">Next Story</p>
                <p className="text-white group-hover:text-white/70 transition-colors flex items-center gap-2">
                  {nextStory.client_name}
                  <ArrowRight className="w-4 h-4" />
                </p>
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      {/* Gallery Modal */}
      {galleryModal !== null && (
        <GalleryModal 
          images={story.gallery_images} 
          initialIndex={galleryModal} 
          onClose={() => setGalleryModal(null)} 
        />
      )}

      {/* Video Modal */}
      {videoModal && (
        <VideoModal video={videoModal} onClose={() => setVideoModal(null)} />
      )}
    </div>
  );
};

export default StoryDetailPage;
